import { usePubNub } from "pubnub-react";
import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ChatConversationChannelPresence,
  ChatConversationPresence,
} from "../../../api/types/Chat";
import svgIcons from "../../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../../constants/constants";
import { CHAT_CHANNEL_TYPES } from "../constants";
import { useChatContext } from "../contexts/chat.context";
import {
  TPubNubConversationMessage,
  TPubNubFileMessage,
  TPubNubTextMessage,
} from "../contexts/types/pubnubChat";
import ChatConversationMessage from "./chatConversationMessage";
import Picker from "@emoji-mart/react";
import emojiMartData from "@emoji-mart/data";
import ChatGroupMembersDropdown from "./chatGroupMembersDropdown";
import PagewideMessageDisplay from "../../../components/pagewideMessageDisplay";

type TChatConversationProps = {
  wrapperClass?: string;
};

const ChatConversation: FC<TChatConversationProps> = ({
  wrapperClass = "col-span-8 h-[calc(100vh-160px)]",
}) => {
  const pubnub = usePubNub();
  const { t } = useTranslation("translation", { keyPrefix: "chatSection" });

  const {
    subscribedChannels,
    selectedChatChannel,
    conversations,
    addPresence,
    fileBeingUploaded,
    setFileBeingUploaded,
  } = useChatContext();

  const [channels, setChannels] = useState([selectedChatChannel]); // 'awesome-channel'
  const [conversationMessages, setConversationMessages] = useState<
    TPubNubConversationMessage[]
  >([]);
  const [inputText, setInputText] = useState<string>("");
  const conversationMessagesWrapperRef = React.createRef<HTMLDivElement>();

  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);

  const [showGroupMembers, setShowGroupMembers] = useState<boolean>(false);

  // initialize local state
  useEffect(() => {
    setChannels([selectedChatChannel]);
  }, [selectedChatChannel]);

  // update conversation messages when selected channel changes
  useEffect(() => {
    if (selectedChatChannel in conversations) {
      setConversationMessages(conversations[selectedChatChannel]);
    }
  }, [selectedChatChannel, conversations]);

  // publish a message to the channel
  const sendNewMessage = (message: string) => {
    if (message) {
      pubnub
        .publish({
          channel: channels[0],
          message: { sender: pubnub.getUUID(), message } as TPubNubTextMessage,
        })
        .then(() => setInputText(""));
    }
  };

  // on new message, scroll to bottom
  const scrollToBottom = () => {
    if (!conversationMessagesWrapperRef.current) return;
    conversationMessagesWrapperRef.current.scrollTop =
      conversationMessagesWrapperRef.current.scrollHeight;
  };
  useEffect(scrollToBottom, [
    conversationMessages,
    conversationMessagesWrapperRef,
  ]);

  // on mount/unmount record the timestamp
  const recordPresence = (clearUnreadCount = false) => {
    addPresence((presence: ChatConversationPresence) => {
      const newPresence = {
        ...presence,
        [selectedChatChannel]: {
          ...presence[selectedChatChannel],
          lastReadMessageTimestamp: new Date().getTime(),
          unreadMessageCount: clearUnreadCount
            ? 0
            : (presence[selectedChatChannel]?.unreadMessageCount ?? 0),
        } as ChatConversationChannelPresence,
      };
      if (APP_CONFIG.DEBUG.MESSAGING)
        console.log("Record New Presence:", newPresence);
      return newPresence;
    });
  };
  useEffect(() => {
    // record presence and clear count
    recordPresence(true);

    // scroll to bottom
    scrollToBottom();

    // focus on the message input
    messageInputRef?.current?.focus();

    return () => {
      // record presence
      recordPresence();
    };
  }, []);

  // on focus clear unread count
  useEffect(() => {
    const onFocus = () => {
      recordPresence(true);
    };
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  if (!!!selectedChatChannel) {
    return (
      <div className={`${wrapperClass} flex items center justify-center`}>
        <div className="text-center text-gray-500">
          Select a channel to start chatting
        </div>
      </div>
    );
  }

  const thisChannel = subscribedChannels.find(
    (channel) => channel.channel_id === selectedChatChannel
  );
  const messageInputRef = React.createRef<HTMLInputElement>();
  const fileUploadAttachmentRef = React.createRef<HTMLInputElement>();
  const fileUploadImageRef = React.createRef<HTMLInputElement>();

  const composeActions = [
    {
      icon: svgIcons.ChatAttachmentIcon,
      title: "Attachment", // used in conditional logic
      action: () => {
        console.log("Attachment");
      },
    },
    {
      icon: svgIcons.ChatImageIcon,
      title: "Image", // used in conditional logic
      action: () => {
        console.log("Image");
      },
    },
    {
      icon: svgIcons.ChatEmojiIcon,
      title: "Emoji",
      action: (e: React.MouseEvent<HTMLButtonElement>) => {
        // blur the button
        e.currentTarget.blur();
        // launch emoji picker
        setShowEmojiPicker(!showEmojiPicker);
      },
    },
  ];

  const onFileUploadAttachmentCallback = async (
    e: React.ChangeEvent<HTMLInputElement>,
    isImage: boolean
  ) => {
    console.log("Attachment", e.target.files);
    // upload file to pubnub
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      console.log("Reader OnLoad:", e);
      const fileData = e.target?.result;
      // check file data
      if (!fileData) {
        console.log("no fileData, returning");
        toast.error(
          "Error reading file data. Please make sure that the file is not corrupted."
        );
        return;
      }
      // check file size
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > APP_CONFIG.CHAT.PUBNUB_MAX_UPLOAD_SIZE_MB) {
        console.log("file size is too large:", fileSizeInMB);
        toast.error(
          `File size is too large. Please upload a file less than ${APP_CONFIG.CHAT.PUBNUB_MAX_UPLOAD_SIZE_MB}MB in size.`
        );
        return;
      }
      // check file type
      if (isImage && !file.type.startsWith("image/")) {
        console.log("file type is not an image:", file.type);
        toast.error("Please upload an image file.");
        return;
      }
      console.log("sending file to pubnub", file);
      setFileBeingUploaded(file);
      try {
        const message = {
          filename: file.name,
          filesize: file.size,
          filetype: file.type,
          message: "Attachment",
          sender: pubnub.getUUID(),
        } as TPubNubFileMessage;
        const uploadResponse = await pubnub.sendFile({
          channel: channels[0],
          file: file,
          message,
        });
        console.log(
          `${isImage ? "Image" : "Attachment"} Upload Response:`,
          uploadResponse
        );
        setFileBeingUploaded(null);
      } catch (error) {
        console.error("Error uploading file:", error);
        toast.error("Error uploading file. Please try again.");
        setFileBeingUploaded(null);
      }
    };
    reader.readAsDataURL(file);
    reader.onerror = (e) => {
      console.error("FileReader Error:", e);
      setFileBeingUploaded(null);
    };
  };

  // append emoji to input text
  const onEmojiSelect = (emoji: any) => {
    setInputText(inputText + emoji.native);
    setShowEmojiPicker(false);
    messageInputRef?.current?.focus();
  };

  return (
    <div className={`${wrapperClass}`}>
      <div className="w-full h-full flex flex-col border-l border-l-black/10 pl-4">
        {/* Header */}
        <div className="flex items-center h-11 border-b border-b-black/10 text-accent-blue-dark font-bold text-lg px-3 py-2">
          {thisChannel?.channel_type === CHAT_CHANNEL_TYPES.GROUP && (
            <img
              className="bg-accent-blue-pale rounded-full size-8 inline-block mr-4 z-10 outline outline-[6px] outline-accent-blue-pale"
              src={svgIcons.GroupChatIcon}
            />
          )}
          {thisChannel?.channel_name ||
            thisChannel?.display_info?.name ||
            "Unknown Channel"}
          <span className="flex-grow"></span>
          {thisChannel?.channel_type === CHAT_CHANNEL_TYPES.GROUP && (
            <span className="flex flex-col text-xs text-right font-normal relative">
              <span className="">
                {t("members_w_count", {
                  count: thisChannel?.members_info.length,
                })}
              </span>
              <a
                className="text-accent-blue-bright underline"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowGroupMembers(!showGroupMembers);
                }}
              >
                {t("view_details")}
              </a>
              {showGroupMembers && (
                <>
                  <div
                    className="fixed top-0 left-0 w-full h-screen bg-white/70 backdrop-blur-[1px] z-popup-overlay"
                    onClick={() => setShowGroupMembers(!showGroupMembers)}
                  ></div>
                  <ChatGroupMembersDropdown
                    title={t("group_members")}
                    values={
                      thisChannel?.members_info.map((member) => member.name) ||
                      []
                    }
                  />
                </>
              )}
            </span>
          )}
        </div>

        {/* Messages */}
        <div
          ref={conversationMessagesWrapperRef}
          className="flex flex-col gap-3 flex-grow overflow-auto bg-white mt-2"
        >
          {/* {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && <pre className="absolute text-[8px] pointer-events-none touch-none bottom-0 z-10">{JSON.stringify(conversationMessages, null, 2)}</pre>} */}
          {conversationMessages.length > 0 ? (
            <>
              {conversationMessages.map((message, index) => {
                return (
                  <ChatConversationMessage
                    key={`message-${index}`}
                    message={message}
                  />
                );
              })}
            </>
          ) : (
            <PagewideMessageDisplay
              title="Start a conversation"
              description="Type a message to begin the conversation"
            />
          )}
          <div className="w-full h-8 flex-shrink-0" />
        </div>

        {/* Footer - Message Input, Submit and More Buttons */}
        <div className={`flex relative`}>
          {/* Message Input and Submit */}
          <span
            className={`relative flex-grow${showEmojiPicker ? " z-modal" : ""}`}
          >
            <input
              ref={messageInputRef}
              type="text"
              className="w-full text-sm border border-black/10 rounded-lg ps-4 pe-12 h-14 enabled:outline-accent-blue-dark"
              placeholder={
                !!fileBeingUploaded
                  ? t("message_input_placeholder_uploading")
                  : t("message_input_placeholder")
              }
              value={inputText}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                sendNewMessage(inputText);
              }}
              onChange={(e) => setInputText(e.target.value)}
              onClick={() => setShowEmojiPicker(false)}
              disabled={!!fileBeingUploaded}
            />
            <button
              className={
                `absolute right-2 top-1/2 -translate-y-1/2 size-9 pl-1 flex items-center justify-center group transition-all ` +
                `bg-white enabled:hover:bg-accent-blue-dark enabled:focus:bg-accent-blue-dark rounded-lg`
              }
              onClick={(e) => {
                e.preventDefault();
                sendNewMessage(inputText);
              }}
              disabled={!!fileBeingUploaded}
            >
              <img
                className={`size-8 transition-all group-enabled:group-focus:grayscale-0 group-enabled:group-focus:brightness-0 group-enabled:group-focus:invert group-enabled:group-hover:grayscale-0 group-enabled:group-hover:brightness-0 group-enabled:group-hover:invert${!!fileBeingUploaded ? " grayscale opacity-50" : ""}`}
                src={svgIcons.SendChatMessageIcon}
                alt="send"
              />
            </button>
          </span>

          {/* More Buttons */}
          <div
            className={`flex items-center gap-2 ml-4 relative${!!fileBeingUploaded ? " grayscale opacity-50 pointer-events-none touch-none" : ""}`}
          >
            {composeActions.map((action, index) => {
              const buttonClassName = `size-9 flex items-center justify-center p-1 group transition-all bg-white enabled:hover:bg-accent-blue-dark enabled:focus:bg-accent-blue-dark rounded-lg`;
              const imageClassName = `size-8 transition-all group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert`;
              const imageClassNamePeer = `peer-focus:grayscale-0 peer-focus:brightness-0 peer-focus:invert peer-hover:grayscale-0 peer-hover:brightness-0 peer-hover:invert`;
              return (
                <React.Fragment key={`compose-action-${index}`}>
                  {["Attachment", "Image"].includes(action.title) ? (
                    <label
                      htmlFor={`compose-action-${action.title}`}
                      className={`relative overflow-hidden ${buttonClassName}`}
                    >
                      <input
                        className="absolute w-full h-full opacity-0 z-[2] cursor-pointer peer appearance-none"
                        id={`compose-action-${action.title}`}
                        type="file"
                        accept={action.title === "Image" ? "image/*" : ""}
                        ref={
                          action.title === "Image"
                            ? fileUploadImageRef
                            : fileUploadAttachmentRef
                        }
                        onChange={(e) =>
                          onFileUploadAttachmentCallback(
                            e,
                            action.title === "Image"
                          )
                        }
                      />
                      <span className="absolute top-0 left-0 w-full h-full z-0 rounded-lg transition-all peer-hover:bg-accent-blue-dark peer-focus:bg-accent-blue-dark" />
                      <img
                        className={`pointer-events-none touch-none z-[1] ${imageClassName} ${imageClassNamePeer}`}
                        src={action.icon}
                        alt={action.title}
                      />
                    </label>
                  ) : (
                    <button
                      className={
                        buttonClassName
                          .replace(
                            "bg-white",
                            showEmojiPicker ? "bg-accent-blue-dark" : "bg-white"
                          )
                          .replace(
                            /enabled\:hover\:/g,
                            showEmojiPicker ? "enabled:" : "enabled:hover:"
                          ) + ` relative${showEmojiPicker ? " z-popup" : ""}`
                      }
                      onClick={action.action}
                      title={action.title}
                    >
                      <img
                        className={imageClassName.replace(
                          /group\-hover\:/g,
                          showEmojiPicker ? "" : "group-hover:"
                        )}
                        src={action.icon}
                        alt={action.title}
                      />
                    </button>
                  )}
                </React.Fragment>
              );
            })}
            {showEmojiPicker && (
              <>
                <div
                  className="fixed top-0 left-0 w-full h-screen bg-white/70 backdrop-blur-[1px] z-popup-overlay"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                ></div>
                <div className="absolute bottom-full mb-2 right-1 z-popup">
                  <Picker data={emojiMartData} onEmojiSelect={onEmojiSelect} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatConversation;
